import { OrderGuideUploadMode } from '@choco-admin/types';
import { useCallback, useState } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import { CsvIssue, GroupUploadResult, UploadGroup } from '../types';
import { parseUploadFile } from '../utils/parseUploadFile';
import { validateGroups } from '../utils/validation';

import { useChatOrderGuides } from './useChatOrderGuides';
import { useOrderGuideBatchUpdate } from './useOrderGuideBatchUpdate';

export type UploadPhase =
  | 'idle'
  | 'processing'
  | 'review'
  | 'uploading'
  | 'done';

interface UploadState {
  phase: UploadPhase;
  fileName: string | null;
  /** Fatal file problem (unreadable/unparseable) — no review possible */
  fileError: string | null;
  /** Chat lookup network failure — retryable */
  lookupError: string | null;
  issues: CsvIssue[];
  groups: UploadGroup[];
  results: GroupUploadResult[];
  /** True when the real (non-dry-run) mutations were attempted */
  didWrite: boolean;
}

const INITIAL_STATE: UploadState = {
  phase: 'idle',
  fileName: null,
  fileError: null,
  lookupError: null,
  issues: [],
  groups: [],
  results: [],
  didWrite: false,
};

/**
 * State machine for the upload flow:
 * idle → processing (parse, validate, chat lookups) → review → uploading → done.
 * Uploading dry-runs every group first; nothing is written unless all
 * groups pass backend validation.
 */
export const useOrderGuideUpload = () => {
  const [state, setState] = useState<UploadState>(INITIAL_STATE);
  const [mode, setMode] = useState<OrderGuideUploadMode>('Update');
  const { fetchChatOrderGuides } = useChatOrderGuides();
  const { batchUpdate } = useOrderGuideBatchUpdate();
  const { addNotification } = useNotification();

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
    setMode('Update');
  }, []);

  const processFile = useCallback(
    async (file: File) => {
      setState({ ...INITIAL_STATE, phase: 'processing', fileName: file.name });

      const outcome = await parseUploadFile(file);

      if (outcome.status === 'fatal') {
        setState({
          ...INITIAL_STATE,
          fileName: file.name,
          fileError: outcome.fileError,
        });
        return;
      }

      if (outcome.status === 'headerErrors') {
        setState({
          ...INITIAL_STATE,
          phase: 'review',
          fileName: file.name,
          issues: outcome.issues,
        });
        return;
      }

      const { issues, groups } = outcome;
      try {
        const chatInfoById = await fetchChatOrderGuides(
          groups.map((group) => group.chatId)
        );
        const groupValidation = validateGroups(groups, chatInfoById);
        setState({
          ...INITIAL_STATE,
          phase: 'review',
          fileName: file.name,
          issues: [...issues, ...groupValidation.issues],
          groups: groupValidation.groups,
        });
      } catch (lookupFailure) {
        setState({
          ...INITIAL_STATE,
          phase: 'review',
          fileName: file.name,
          issues,
          groups,
          lookupError:
            lookupFailure instanceof Error
              ? lookupFailure.message
              : 'Failed to look up the chats in this file.',
        });
      }
    },
    [fetchChatOrderGuides]
  );

  const upload = useCallback(async () => {
    const { groups } = state;
    setState((previous) => ({
      ...previous,
      phase: 'uploading',
      results: groups.map((group) => ({
        group,
        status: 'pending',
        summary: null,
        errorMessage: null,
      })),
    }));

    const setResult = (index: number, result: Partial<GroupUploadResult>) =>
      setState((previous) => ({
        ...previous,
        results: previous.results.map((entry, entryIndex) =>
          entryIndex === index ? { ...entry, ...result } : entry
        ),
      }));

    const runGroup = async (group: UploadGroup, dryRun: boolean) => {
      const result = await batchUpdate({
        chatId: group.chatId,
        orderGuideId: group.orderGuideId ?? group.targetGuide?.id ?? null,
        mode,
        dryRun,
        items: group.items,
      });
      if (!result.ok) return result;
      if (result.summary.errors.length > 0) {
        return {
          ok: false as const,
          errorMessage: `${result.summary.errors.length} product(s) failed backend validation.`,
          summary: result.summary,
        };
      }
      return result;
    };

    // Stage 1 — dry-run everything; write nothing unless all groups pass.
    // Groups run sequentially (reduce chain) to go easy on the backend.
    let dryRunFailed = false;
    await groups.reduce(async (previous, group, index) => {
      await previous;
      setResult(index, { status: 'validating' });
      const result = await runGroup(group, true);
      if (result.ok) {
        setResult(index, { status: 'ready' });
      } else {
        dryRunFailed = true;
        setResult(index, {
          status: 'failed',
          errorMessage: result.errorMessage,
          summary: 'summary' in result ? result.summary : null,
        });
      }
    }, Promise.resolve());

    if (dryRunFailed) {
      setState((previous) => ({
        ...previous,
        phase: 'done',
        didWrite: false,
        results: previous.results.map((entry) =>
          entry.status === 'failed' ? entry : { ...entry, status: 'skipped' }
        ),
      }));
      addNotification({
        title: 'Nothing uploaded',
        message:
          'Backend validation failed for at least one order guide. Fix the reported products and try again.',
        type: 'error',
      });
      return;
    }

    // Stage 2 — all groups validated; execute for real, sequentially.
    let failures = 0;
    await groups.reduce(async (previous, group, index) => {
      await previous;
      setResult(index, { status: 'uploading' });
      const result = await runGroup(group, false);
      if (result.ok) {
        setResult(index, { status: 'success', summary: result.summary });
      } else {
        failures += 1;
        setResult(index, {
          status: 'failed',
          errorMessage: result.errorMessage,
          summary: 'summary' in result ? result.summary : null,
        });
      }
    }, Promise.resolve());

    setState((previous) => ({ ...previous, phase: 'done', didWrite: true }));
    if (failures === 0) {
      addNotification({
        title: 'Upload complete',
        message: `${groups.length} order guide(s) updated successfully.`,
        type: 'success',
      });
    } else {
      addNotification({
        title: 'Upload finished with errors',
        message: `${failures} of ${groups.length} order guide(s) failed — see the results below.`,
        type: 'error',
      });
    }
  }, [state, mode, batchUpdate, addNotification]);

  return {
    ...state,
    mode,
    setMode,
    processFile,
    upload,
    reset,
  };
};
